import { SiteConfig } from '@utils';
import {
  SITE_CONFIG_API_URL,
} from '@config/constants';

export const getCaptchaSiteKey = async (): Promise<
| { fullMatchedDomain: string; headerFooterUrl: string }
| { headerUrl: string; footerUrl: string; headerFooterCSS: string, source: string; }
| undefined
> => {
  const { siteId } = SiteConfig.getConfig();
  const { hostname } = window.location;
  const encodedDomain = btoa(hostname);
  const apiUrl = `${SITE_CONFIG_API_URL}?domain=${encodedDomain}`;
  try {
    const res = await fetch(apiUrl);
    const config = await res.json();
    const fullMatchedDomain = `https://${hostname}`;
    const siteData = config[siteId];

    if (siteData.footerUrl && siteData.headerUrl) {
      const headerUrl = siteData?.headerUrl || '';
      const footerUrl = siteData?.footerUrl || '';
      const headerFooterCSS = siteData?.headerFooterCSS || '';
      return {
        headerUrl,
        footerUrl,
        headerFooterCSS,
        source: siteData?.source || '',
      };
    }
    const headerFooterUrl = siteData?.headerUrl || '';
    return {
      fullMatchedDomain,
      headerFooterUrl,
    };
  } catch (err) {
    // Failed to fetch site config; error suppressed for production.
    return undefined;
  }
};

export const getLocalizedHeaderURL = (
  headerUrl: string,
  country: string,
  lang: string,
) => {
  return headerUrl.replace(
    /\/[a-z]{2}\/[a-z]{2}\//,
    `/${country?.toLocaleLowerCase?.()}/${lang?.toLocaleLowerCase?.()}/`,
  );
};

type CaptchaSeparateReturn = {
  source: string;
  headerUrl: string;
  footerUrl: string;
  headerFooterCSS?: string;
};

export const buildHeaderFooterUrl = async (): Promise<string | CaptchaSeparateReturn> => {
  const { lang, iso } = SiteConfig.getConfig();

  const captchaConfig = await getCaptchaSiteKey();
  if (!captchaConfig) {
    throw new Error('Header Footer URL is undefined');
  }
  // If the site config already provides separate header/footer URLs, return it as-is
  if (captchaConfig && 'headerUrl' in captchaConfig && 'footerUrl' in captchaConfig) {
    return captchaConfig;
  }

  // Legacy/combined behaviour: localize and return a single headerFooterUrl string
  const { headerFooterUrl } = captchaConfig;

  if (!headerFooterUrl) {
    throw new Error('Header Footer URL is undefined');
  }

  let baseUrl: string;
  if (
    headerFooterUrl.includes('{country}')
    && headerFooterUrl.includes('{lang}')
  ) {
    baseUrl = headerFooterUrl.replace('{country}', iso).replace('{lang}', lang);
  } else {
    baseUrl = getLocalizedHeaderURL(headerFooterUrl, iso, lang);
  }
  return baseUrl;
};

export const triggerAEMHeaderCartIcon = () => {
  const { locale, siteId } = SiteConfig.getConfig();

  document.dispatchEvent(new CustomEvent('wly-cart:updated', {
    detail: {
      locale,
      site: siteId,
    },
  }));
};

// eslint-disable-next-line max-len
export const getInlineStyleContents = (documentNode: Document): string[] => Array.from(documentNode.querySelectorAll('style'))
  .map((style) => style.textContent?.trim() ?? '')
  .filter(Boolean);

const INLINE_STYLE_MARKER = 'data-wly-aem-inline-style';

export const injectInlineStyles = (styleContents: string[]): void => {
  Array.from(new Set(styleContents.filter(Boolean))).forEach((css) => {
    const style = document.createElement('style');

    style.setAttribute(INLINE_STYLE_MARKER, 'true');
    style.textContent = css;

    document.head.appendChild(style);
  });
};
